import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Info, Plus, Minus } from 'lucide-react';
interface IconProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
}
// Minimal premium dive-mask icon (lucide-style: stroke, currentColor, round caps)
function DiveMask({
  size = 24,
  strokeWidth = 1.5,
  className
}: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" data-id="element-116">
      <path d="M4 8.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a3 3 0 0 1-3 3h-1.6a2 2 0 0 1-1.7-1l-.9-1.5a1.3 1.3 0 0 0-2.2 0l-.9 1.5a2 2 0 0 1-1.7 1H7a3 3 0 0 1-3-3z" data-id="element-117" />
      <path d="M4 9.5 2.5 9M20 9.5 21.5 9" data-id="element-118" />
      <path d="M9.5 17.5a3.5 3.5 0 0 0 5 0" data-id="element-119" />
    </svg>;
}
const etapas = [{
  icon: DiveMask,
  title: 'Aguas confinadas 1',
  contenido: ['En tu primer contacto con el mundo subacuático aprenderás a utilizar el equipo de buceo y a respirar bajo el agua de forma cómoda y segura.', 'Comenzarás a familiarizarte con el entorno acuático y descubrirás cómo funciona el equipamiento que utilizarás durante todo el curso.', 'El objetivo principal es ganar confianza y disfrutar tu primera experiencia respirando bajo el agua.']
}, {
  icon: DiveMask,
  title: 'Aguas confinadas 2',
  contenido: ['Continuarás desarrollando confianza mientras aprendes habilidades fundamentales para desenvolverte bajo el agua.', 'Comenzarás a controlar mejor tu posición, tus movimientos y tu interacción con el entorno.', 'Cada ejercicio está diseñado para que te sientas cada vez más cómodo y seguro.']
}, {
  icon: DiveMask,
  title: 'Aguas confinadas 3',
  contenido: ['Profundizarás las habilidades aprendidas anteriormente y comenzarás a desarrollar una mayor autonomía como buzo.', 'Aprenderás procedimientos utilizados diariamente por buzos certificados en todo el mundo.', 'La práctica constante permitirá que cada habilidad se vuelva natural.']
}, {
  icon: DiveMask,
  title: 'Aguas confinadas 4',
  contenido: ['En esta etapa trabajarás situaciones reales que podrían presentarse durante una inmersión recreativa.', 'El objetivo es que desarrolles confianza, control y capacidad de respuesta en diferentes escenarios.', 'Todo se realiza en un entorno controlado y supervisado por instructores certificados.']
}, {
  icon: DiveMask,
  title: 'Aguas confinadas 5',
  contenido: ['La última sesión en piscina está enfocada en consolidar todo lo aprendido.', 'Repasarás habilidades, perfeccionarás técnicas y llegarás preparado para dar el siguiente paso hacia las aguas abiertas.', 'Es el puente entre el entrenamiento y tus futuras inmersiones de certificación.']
}, {
  icon: Compass,
  title: 'Aguas abiertas 1 y 2',
  contenido: ['Comenzarás tus primeras inmersiones en un entorno natural aplicando todo lo aprendido durante las sesiones en piscina.', 'Experimentarás la sensación real del buceo mientras continúas desarrollando confianza y control.', 'Estas inmersiones forman parte de las dos primeras certificaciones en aguas abiertas requeridas por PADI.']
}, {
  icon: Compass,
  title: 'Aguas abiertas 3 y 4',
  contenido: ['Durante las últimas inmersiones demostrarás las habilidades adquiridas durante todo el entrenamiento.', 'Una vez completadas satisfactoriamente obtendrás tu certificación internacional PADI Open Water Diver.', 'A partir de ese momento podrás bucear en centros de buceo de todo el mundo y comenzar una nueva etapa de aventuras bajo el agua.']
}];
export function EntrenamientoPasoAPaso() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-20 sm:py-24 px-6 bg-brand-background relative" data-id="element-120">
      <div className="max-w-3xl mx-auto" data-id="element-121">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12 sm:mb-14 max-w-2xl mx-auto" data-id="element-122">
          <h3 className="text-brand-primary font-heading font-bold text-3xl sm:text-4xl md:text-5xl uppercase tracking-tight mb-4" data-id="element-123">
            Tu entrenamiento paso a paso
          </h3>
          <p className="text-brand-details/90 text-lg font-light" data-id="element-124">
            Desde tu primera respiración bajo el agua hasta convertirte en un
            buzo certificado internacionalmente.
          </p>
        </motion.div>

        {/* Transición visual: imagen real de entrenamiento */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.97
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="relative w-full rounded-3xl overflow-hidden mb-12 sm:mb-14 aspect-[16/10] sm:aspect-[21/9]" data-id="element-125">
          <img src="https://cdn.magicpatterns.com/uploads/5U1EYgdbnGGKvkccue2fiJ/opcion_hero_numero_6.jpg" alt="Alumnas con equipo de buceo listas para entrenar junto al agua" className="w-full h-full object-cover object-center" data-id="element-126" />
          <div className="absolute inset-0 bg-brand-background/45 mix-blend-multiply" data-id="element-127"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-background/60 via-transparent to-transparent" data-id="element-128"></div>
        </motion.div>

        <div className="space-y-3 sm:space-y-4" data-id="element-129">
          {etapas.map((item, index) => {
          const isOpen = openIndex === index;
          const Icon = item.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.4,
            delay: index * 0.06
          }} className={`rounded-2xl border bg-brand-dark overflow-hidden transition-colors ${isOpen ? 'border-brand-primary/50' : 'border-brand-primary/20'}`} data-id="element-130">
                <button onClick={() => setOpenIndex(isOpen ? null : index)} aria-expanded={isOpen} className="w-full flex items-center gap-4 p-5 sm:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/60 rounded-2xl" data-id="element-131">
                  <span className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary shrink-0" data-id="element-132">
                    <Icon size={24} strokeWidth={1.5} data-id="element-133" />
                  </span>
                  <span className="flex-grow text-white font-bold text-lg sm:text-xl" data-id="element-134">
                    {item.title}
                  </span>
                  <span className="text-brand-primary shrink-0" data-id="element-135">
                    {isOpen ? <Minus size={22} data-id="element-136" /> : <Plus size={22} data-id="element-137" />}
                  </span>
                </button>

                <AnimatePresence initial={false} data-id="element-138">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-139">
                      <div className="px-5 sm:px-6 pb-6 pl-[4.25rem] sm:pl-[5.5rem] space-y-3 text-white/70 leading-relaxed" data-id="element-140">
                        {item.contenido.map((p, i) => <p key={i} data-id="element-141">{p}</p>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        {/* Highlighted note */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6
      }} className="mt-8 rounded-2xl border border-brand-primary/30 bg-brand-primary/5 backdrop-blur-sm p-6 sm:p-7 flex gap-4" data-id="element-142">
          <Info size={24} className="text-brand-primary shrink-0 mt-1" data-id="element-143" />
          <div className="space-y-3 text-white/80 leading-relaxed text-sm sm:text-base" data-id="element-144">
            <p data-id="element-145">
              En la modalidad{' '}
              <span className="text-white font-semibold" data-id="element-146">
                Open Water Completo
              </span>
              , las 4 inmersiones en aguas abiertas se realizan en dos jornadas
              diferentes e incluyen todo el equipamiento necesario para
              completar la certificación.
            </p>
            <p data-id="element-147">
              La modalidad{' '}
              <span className="text-white font-semibold" data-id="element-148">Referral</span> permite
              completar estas inmersiones posteriormente en cualquier centro
              PADI habilitado del mundo.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}